"use client"

import React, { useMemo } from "react"
import ReactECharts from "echarts-for-react"

const MAX_LABEL_LENGTH = 15
const VISIBLE_SKUS = 25   // how many SKUs visible at once

const HistoricChartTwo = ({
  sales_summary_sku_wise = [],
  destinations = [],
  skus = []
}) => {

  /* 1. Filter data */
  const filteredData = useMemo(() => {
    return sales_summary_sku_wise.filter(item => {
      const destinationMatch =
        !destinations.length ||
        destinations.includes(item.destination_store_name)

      const skuMatch =
        !skus.length || skus.includes(item.sku_code)

      return destinationMatch && skuMatch
    })
  }, [sales_summary_sku_wise, destinations, skus])

  /* 2. Total quantity per SKU (for sorting) */
  const skuTotals = useMemo(() => {
    const map = {}
    filteredData.forEach(d => {
      map[d.sku_code] = (map[d.sku_code] || 0) + d.sale_quantity
    })
    return map
  }, [filteredData])

  /* 3. Sorted SKU list (DESC by sales) */
  const skuList = useMemo(() => {
    return Object.keys(skuTotals)
      .sort((a, b) => skuTotals[b] - skuTotals[a])
  }, [skuTotals])

  /* 4. Truncated labels */
  const displaySkuList = useMemo(() => {
    return skuList.map(sku =>
      sku.length > MAX_LABEL_LENGTH
        ? sku.slice(0, MAX_LABEL_LENGTH) + "…"
        : sku
    )
  }, [skuList])

  /* 5. Destination list */
  const storeList = useMemo(() => {
    return [...new Set(filteredData.map(d => d.destination_store_name))]
  }, [filteredData])

  /* 6. Quantity lookup map */
  const quantityMap = useMemo(() => {
    const map = {}
    filteredData.forEach(d => {
      map[`${d.destination_store_name}__${d.sku_code}`] =
        (map[`${d.destination_store_name}__${d.sku_code}`] || 0) +
        d.sale_quantity
    })
    return map
  }, [filteredData])

  /* 7. Series */
  const series = useMemo(() => {
    return storeList.map(store => ({
      name: store,
      type: "bar",
      stack: "total",
      barCategoryGap: "40%",
      data: skuList.map(
        sku => quantityMap[`${store}__${sku}`] ?? 0
      )
    }))
  }, [storeList, skuList, quantityMap])

  /* 8. Chart options */
  const option = {
    title: {
      text: "Sales Summary SKUs Wise",
      left: "center",
      textStyle: { fontWeight: "normal", fontSize: 18 }
    },

    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      formatter: params => {
        const index = params[0].dataIndex
        const fullSku = skuList[index]

        let total = 0
        let html = `<b>SKU:</b> ${fullSku}<br/>`

        params.forEach(p => {
          if (p.data > 0) {
            html += `${p.seriesName}: ${p.data}<br/>`
            total += p.data
          }
        })

        return html + `<b>Total:</b> ${total}`
      }
    },

    legend: {
      type: "scroll",
      bottom: 10
    },

    grid: {
      left: 160,
      right: 20,
      bottom: 80,
      top: 60
    },

    xAxis: {
      type: "value",
      name: "Quantity",
      nameLocation: "middle",
      nameGap: 30
    },

    yAxis: {
      type: "category",
      inverse: true,              // ⭐ biggest SKU at top
      data: displaySkuList,
      name: "SKU",
      nameGap: 90,
      axisLabel: {
        width: 140,
        overflow: "truncate"
      }
    },

    /* ⭐ THIS IS THE KEY FIX */
    dataZoom: [
      {
        type: "slider",
        yAxisIndex: 0,
        right: 5,
        start: 0,
        end: Math.min((VISIBLE_SKUS / skuList.length) * 100, 100)
      }
    ],

    series
  }

  return (
    <ReactECharts
      option={option}
      notMerge={true}
      style={{ height: 700, width: "100%", padding:0 }}
    />
  )
}

export default HistoricChartTwo
